/**
 * FilePreviewOverlay — the universal in-platform file preview popup.
 * (2026-08-01 directive: every deliverable type opens user-friendly with per-type
 * controls; 2026-08-02 amendments: opens for ALL file types every time — the binary
 * card with download options lives INSIDE the previewer; defaults LARGE; sits above
 * the fully-expanded chat surface; and can hand itself off to the app-level floating
 * multitask window via the Float control.)
 *
 * Renderers live in PreviewContent (shared with FloatingPreviewWindow): image
 * zoom/pan/rotate, native video/audio players, blob-iframe PDF paging, sandboxed
 * interactive 3D, zero-permission static HTML, mono text with copy/wrap, and the
 * download card for opaque binaries. Download + open-in-tab available for every kind.
 */
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { previewKindLabel } from './previewKind';
import { usePreview, PreviewBody, ToolButton, formatSize, openableHref } from './PreviewContent';
import { useFloatingPreview } from './FloatingPreviewContext';
import { OpenChatBesideButton, type PreviewOriginContext } from './previewOrigin';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';
import { PREVIEW_MODAL_Z, PREVIEW_MODAL_VIEWPORT_MARGIN_PX } from './windowGeometry';
import { CHAT_LAYOUT, type SerializedAttachment } from '../GlobalChatSystem/types';
import { attachmentNoteOf } from '../GlobalChatSystem/attachmentNote';
import {
  DOCKED_RAIL_FOOTPRINT_CSS,
  DOCKED_RAIL_INSET_PX,
  useIsChatDockedCompanion,
} from '../GlobalChatSystem/dockedCompanion';
import { dockedWidthStyle, useDockedWidthChoice } from '../GlobalChatSystem/dockedWidth';

const Backdrop = styled.div<{ $companion?: boolean }>`
  position: fixed; inset: 0; z-index: ${PREVIEW_MODAL_Z};
  background: rgba(10, 14, 24, 0.82);
  display: flex; align-items: center; justify-content: center;
  padding: ${PREVIEW_MODAL_VIEWPORT_MARGIN_PX}px;
  /* Companion glide (2026-08-24): the same 0.4s house curve the chat pane
     and the page shell use, so all three move as one gesture when the chat
     docks or undocks while a preview is open. */
  transition: top 0.4s cubic-bezier(0.2, 0.8, 0.2, 1),
              right 0.4s cubic-bezier(0.2, 0.8, 0.2, 1),
              bottom 0.4s cubic-bezier(0.2, 0.8, 0.2, 1),
              left 0.4s cubic-bezier(0.2, 0.8, 0.2, 1),
              background-color 0.3s ease;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }

  ${props => props.$companion && `
    /* DOCKED-COMPANION SPLIT (owner directive 2026-08-24): while the chat is
       the docked right rail, a preview takes the REMAINING screen instead of
       covering everything — file on the left, conversation on the right, both
       live. The panel mirrors the pane's own vertical geometry (same top and
       bottom edges) so the two read as one two-column view under the nav.
       No dim: the page beside stays visible AND clickable, which is the whole
       point — navigate the app while the file and the chat both stay put.
       Close stays on ✕ and Escape (there is no backdrop strip to click). */
    top: ${CHAT_LAYOUT.DOCKED_TOP}px;
    bottom: ${CHAT_LAYOUT.DOCKED_BOTTOM}px;
    left: ${DOCKED_RAIL_INSET_PX}px;
    right: ${DOCKED_RAIL_FOOTPRINT_CSS};
    padding: 0;
    background: transparent;
  `}

  /* Phones (mobile UX 2026-08-14): the 16px inset bought nothing but lost
     width, and the strip doubled as the ONLY close target once the header ✕
     clipped off-panel — the preview is a true fullscreen sheet instead.
     (The companion split never reaches here — it stands down below its own
     viewport floor, well above 720px — but the ordering keeps phones safe
     even if that floor ever moves.) */
  @media (max-width: 720px) {
    inset: 0;
    padding: 0;
    background: rgba(10, 14, 24, 0.82);
  }
`;

/* Near-full-screen by DEFAULT on every display (2026-08-02 directive): the panel
   fills the viewport minus the backdrop margin — dynamic, no fixed pixel caps. */
const Panel = styled.div`
  background: #101623; color: #e8edf6;
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 14px;
  width: 100%; height: 100%;
  display: flex; flex-direction: column; overflow: hidden;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.5);

  @media (max-width: 720px) {
    border-radius: 0;
    border: none;
    padding-top: env(safe-area-inset-top, 0px);
    padding-bottom: env(safe-area-inset-bottom, 0px);
  }
`;

const Header = styled.div`
  display: flex; align-items: center; gap: 12px;
  padding: 10px 14px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  flex: 0 0 auto;

  /* Phones: five no-shrink pills need ~390px — at 390px the Download pill
     truncated and the ✕ close clipped clean off the panel (owner screenshot
     2026-08-14). The title takes its own row and the buttons WRAP under it;
     Float hides — a drag/resize window is desktop furniture. So is the
     side-by-side split (2026-08-24): a phone has no remaining space to give
     a companion, and MOBILE NEVER DOCKS, so "open chat" would cover the very
     file it was opened from — and it would put a sixth pill back into the
     row this rule exists to protect. */
  @media (max-width: 720px) {
    flex-wrap: wrap;
    row-gap: 8px;
    gap: 8px;
    padding: 10px 12px;

    [data-preview-float],
    [data-preview-open-chat] {
      display: none;
    }
  }
`;

const Title = styled.div`
  min-width: 0; flex: 1;
  font-size: 14px; font-weight: 600;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;

  @media (max-width: 720px) {
    flex-basis: 100%;
  }
`;

const Meta = styled.span`
  color: #93a1b8; font-size: 12px; font-weight: 400; margin-left: 8px;
`;

/** Prest0n's line saying what the file is (EFAX T16, 2026-10-09; attachmentNote.ts), under its name. */
const PreviewNote = styled.span`
  display: block;
  color: #cbd5e1; font-size: 12px; font-weight: 400;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
`;

const Body = styled.div`
  flex: 1; min-height: 0; position: relative;
  display: flex; align-items: stretch; justify-content: center;
  background: #0a0f1a;
`;

export interface FilePreviewOverlayProps {
  file?: File | null;
  serialized?: SerializedAttachment | null;
  /**
   * Where this file came from: message context for the resolver's
   * locator-free fallback (2026-08-09) PLUS the originating conversation
   * (2026-08-24), which the "open chat" control returns to.
   */
  context?: PreviewOriginContext | null;
  onClose: () => void;
}

export const FilePreviewOverlay: React.FC<FilePreviewOverlayProps> = ({ file, serialized, context, onClose }) => {
  const p = usePreview(file, serialized, context);
  const note = attachmentNoteOf(serialized);
  const [wrap, setWrap] = useState(true);
  const bodyRef = React.useRef<HTMLDivElement | null>(null);
  const floating = useFloatingPreview();

  // Is the chat on screen as the docked right rail, with room to share?
  // Read straight off the root-level stamp — no chat state, no provider, so
  // this works from the body portal on any page (dockedCompanion.ts).
  const companion = useIsChatDockedCompanion();
  // The panel clears the rail at the width the person keeps it; it holds that
  // variable itself, since it is not inherited (dockedWidth.ts).
  const dockedWidth = useDockedWidthChoice();

  // Page-scroll lock (2026-08-12 scrolling round): while this full-viewport
  // preview is up, wheel/touch past its edges must not scroll the page (or
  // chat surface) behind the backdrop. Reference-counted — stacks safely
  // with the fullscreen chat's own lock.
  //
  // NOT in companion mode (2026-08-24): there the preview is a panel beside
  // the chat, not a takeover — the page it shares the screen with is meant
  // to stay usable, scroll included. The lock is reference-counted, so
  // flipping this live releases/acquires cleanly as the chat docks.
  useBodyScrollLock(!companion);

  useEffect(() => {
    // Capture phase + stopPropagation: Escape closes THE PREVIEW ONLY — it must
    // never also reach the chat's own handlers (2026-08-02 report: closing the
    // preview collapsed the fully-extended thread).
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      e.preventDefault();
      onClose();
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  const overlay = (
    <Backdrop
      $companion={companion}
      style={companion ? dockedWidthStyle(dockedWidth) : undefined}
      data-preview-companion={companion ? 'true' : 'false'}
      onClick={(e) => {
        // Portal events bubble the REACT tree into chat components — contain them.
        e.stopPropagation();
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      /* Companion mode is deliberately NOT modal: the chat beside it and the
         page under it stay interactive, so announcing a modal would lie to
         assistive tech about what is reachable. */
      aria-modal={companion ? undefined : true}
      aria-label={`Preview of ${p.name}`}
      // The chat's document-level outside-click handler closes the whole chat for
      // clicks outside marked surfaces (portalClickGuard) — the previewer IS a chat
      // surface. Without this, clicking anywhere in the preview collapsed the thread.
      data-prest0n-portal="true"
    >
      <Panel>
        <Header>
          <Title title={note ? `${p.name}\n${note}` : p.name}>
            {p.name}
            <Meta>{previewKindLabel(p.kind)}{p.size ? ` · ${formatSize(p.size)}` : ''}</Meta>
            {note && <PreviewNote data-preview-note="">{note}</PreviewNote>}
          </Title>
          {p.kind === 'text' && p.text != null && (
            <>
              <ToolButton onClick={() => navigator.clipboard?.writeText(p.text!).catch(() => undefined)}>Copy</ToolButton>
              <ToolButton onClick={() => setWrap((w) => !w)}>{wrap ? 'No wrap' : 'Wrap'}</ToolButton>
            </>
          )}
          {(p.kind === 'scene3d' || p.kind === 'pdf' || p.kind === 'html' || p.kind === 'video') && (
            <ToolButton onClick={() => bodyRef.current?.requestFullscreen?.().catch(() => undefined)}>Fullscreen</ToolButton>
          )}
          {/* The file and its conversation, side by side (2026-08-24). Stays
              put after the click: the chat docks right and this panel reflows
              into the space that is left — that IS the requested view. */}
          <OpenChatBesideButton origin={context} />
          {floating && (
            <ToolButton
              data-preview-float=""
              onClick={() => {
                // Origin rides along (2026-08-24): a floated preview used to
                // lose its messageId — breaking the server's locator-free
                // resolve — and now also needs its thread so the window's own
                // "open chat" works days later, on another page.
                floating.openFloating({ kind: 'attachment', file, serialized, title: p.name, context });
                onClose();
              }}
              title="Pop out into a floating window you can move, resize, and keep on any page"
            >
              Float
            </ToolButton>
          )}
          {/* openableHref, not url||blobUrl: window.open on a data: URL is a
              silent no-op in Chrome/Firefox (review 2026-08-14). */}
          {openableHref(p) && (
            <ToolButton onClick={() => window.open(openableHref(p)!, '_blank', 'noopener,noreferrer')}>Open in tab</ToolButton>
          )}
          {(p.url || p.blobUrl) && <ToolButton onClick={p.download}>Download</ToolButton>}
          <ToolButton onClick={onClose} aria-label="close preview">✕</ToolButton>
        </Header>
        <Body ref={bodyRef}><PreviewBody p={p} wrap={wrap} /></Body>
      </Panel>
    </Backdrop>
  );

  return createPortal(overlay, document.body);
};

export default FilePreviewOverlay;
